"use strict";

import cookies from "./cookies";

/**
 * MOB-100 — the cart countdown runs off a server-issued `expireAt`, but the "now" it was
 * compared against came from the device clock. A device whose clock runs ahead of the
 * backend reads a freshly created DRAFT as already expired and loses the cart on the spot;
 * a device running behind keeps it past the moment the backend expires it.
 *
 * The correction is an offset, measured once against a server timestamp that was "now" when
 * the server wrote it. The only such value in the payloads the app already receives is
 * `Orderr.createdAt`, read back immediately after `createOrderrDraft` — the draft was
 * created microseconds earlier by this very session, so `createdAt` is the server's clock at
 * that instant (understated by one round trip, i.e. the cart lives a second or two longer,
 * never shorter). `updatedAt` on a cold restore is NOT such a value: it is the last write,
 * which happened at an unknown distance in the past, and anchoring on it would silently
 * widen the expiry window by the whole idle period.
 *
 * The offset is a property of the device clock, not of the order, so it is persisted next to
 * the `orderrId` cookie: the cart survives a page reload and so must the correction that
 * keeps it alive. Every new draft re-measures it, so a clock the client fixes in the meantime
 * is picked up again. With no measurement on record the offset is zero, which is exactly the
 * behaviour that existed before.
 */
const OFFSET_COOKIE = "serverClockOffset";

function readPersistedOffset() {
  const raw = cookies.get(OFFSET_COOKIE);
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
}

let offsetMs = readPersistedOffset();

class ServerClock {
  /**
   * Measure the offset against a server timestamp taken at the moment the server considered
   * it "now". A missing or unparseable value leaves the previous measurement in place.
   *
   * @param {String|Date|null} serverTimestamp
   */
  static anchor(serverTimestamp) {
    if (serverTimestamp == null) {
      return;
    }

    const serverMs = new Date(serverTimestamp).getTime();
    if (!Number.isFinite(serverMs)) {
      return;
    }

    offsetMs = serverMs - Date.now();
    cookies.set(OFFSET_COOKIE, String(offsetMs));
  }

  /** Drop the measurement and the cookie holding it. */
  static reset() {
    offsetMs = 0;
    cookies.remove(OFFSET_COOKIE);
  }

  /** Difference between the backend clock and the device clock, in milliseconds. */
  static get offsetMs() {
    return offsetMs;
  }

  /** The backend's current time, in milliseconds since the epoch. */
  static now() {
    return Date.now() + offsetMs;
  }

  /** The backend's current time as a Date. */
  static nowDate() {
    return new Date(ServerClock.now());
  }
}

export default ServerClock;
